/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorSubscriptionFeatures } from '../../services/subscriptionService';
import { useVendorAnalytics } from '../../services/vendorAnalyticsService';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/authService';
import { useVendorVerification } from '../../hooks/useVendorVerification';
import RenewalModal from '../../components/shared/RenewalModal';
import EventReminderModal from '../../components/vendor/EventReminderModal';
import ConsultationCountdownBanner from '../../components/vendor/ConsultationCountdownBanner';
import PlatformPerformanceCard from '../../components/vendor/PlatformPerformanceCard';
import AnnouncementCard from '../../components/shared/AnnouncementCard';
import VendorAttentionStack from '../../components/vendor/dashboard/VendorAttentionStack';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import useVendorGettingStarted from '../../hooks/useVendorGettingStarted';
import { VendorGettingStarted, VendorPageHeader } from '../../components/vendor/guidance';
import {
  Calendar,
  DollarSign,
  Star,
  Crown,
  ArrowRight,
  Clock,
  Eye,
  Loader2,
  RefreshCw,
  Video,
  BarChart3,
  Banknote,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import PrivateBookingBadge from '../../components/shared/PrivateBookingBadge';
import { isPrivateBooking, PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../utils/bookingSource';
import useDashboardAmountVisibility from '../../hooks/useDashboardAmountVisibility';
import DashboardAmountVisibilityToggle from '../../components/shared/DashboardAmountVisibilityToggle';

const VendorDashboard = () => {
  const { user } = useAuth();
  const {
    hasActiveSubscription,
    isChecking,
    currentSubscription: subscriptionDetails,
    checkSubscriptionStatus,
    isLoading: loading,
    refetch: refetchSubscription,
  } = useVendorSubscriptionFeatures();
  const { isVendorVerified, isVerificationPending, isLoading: verificationLoading } =
    useVendorVerification();
  const {
    steps: gettingStartedSteps,
    visible: showGettingStarted,
    dismiss: dismissGettingStarted,
  } = useVendorGettingStarted();
  const navigate = useNavigate();
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [showEventReminderModal, setShowEventReminderModal] = useState(false);
  const [period, setPeriod] = useState('month');
  const { amountsVisible, toggleAmountsVisible } = useDashboardAmountVisibility('vendor');

  // Fetch analytics
  const { data: analyticsData, isFetching: analyticsFetching, refetch: refetchAnalytics } = useVendorAnalytics(period);
  const analytics = analyticsData?.data || {};

  // Fetch announcements
  const { data: announcementsData } = useAnnouncements();
  const announcements = announcementsData?.data || [];

  // Fetch recent bookings
  const { data: bookingsData, isLoading: bookingsLoading } = useQuery({
    queryKey: ['vendorRecentBookings'],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings?per_page=5&sort_by=created_at&sort_order=desc');
      return response.data;
    }
  });

  const recentBookings = bookingsData?.data?.data || bookingsData?.data || [];

  // Fetch booking statistics
  const { data: statsData } = useQuery({
    queryKey: ['vendorBookingStats'],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings/statistics');
      return response.data;
    }
  });

  const stats = statsData?.data || {};

  // Fetch pending consultations
  const { data: consultationsData } = useQuery({
    queryKey: ['vendorConsultations', 'pending'],
    queryFn: async () => {
      const response = await api.get('/vendor/consultations', { params: { status: 'pending' } });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
  const pendingConsultations = consultationsData?.data?.data || consultationsData?.data || [];
  const pendingConsultationCount = pendingConsultations.length;

  // Fetch accepted consultations for countdown
  const { data: acceptedConsultationsData } = useQuery({
    queryKey: ['vendorConsultations', 'accepted'],
    queryFn: async () => {
      const response = await api.get('/vendor/consultations', {
        params: { status: 'accepted', per_page: 10 }
      });
      return response.data;
    },
    staleTime: 60 * 1000,
  });

  const acceptedConsultationsRaw =
    acceptedConsultationsData?.data?.data || acceptedConsultationsData?.data || [];

  const upcomingConsultation = React.useMemo(() => {
    if (!acceptedConsultationsRaw || acceptedConsultationsRaw.length === 0) return null;
    const now = new Date();

    const withDates = acceptedConsultationsRaw
      .map((c) => {
        if (!c.scheduled_date) return null;
        // Treat scheduled_date as date-only in local time to avoid timezone shifting issues.
        const dateStr = typeof c.scheduled_date === 'string'
          ? c.scheduled_date.slice(0, 10)
          : String(c.scheduled_date).slice(0, 10);
        const base = new Date(`${dateStr}T00:00:00`);
        if (Number.isNaN(base.getTime())) return null;
        if (c.scheduled_time) {
          const [hRaw, mRaw] = String(c.scheduled_time).split(':');
          const h = Number(hRaw || 0);
          const m = Number(mRaw || 0);
          base.setHours(h, m, 0, 0);
        } else {
          base.setHours(0, 0, 0, 0);
        }
        if (base <= now) return null;
        return { ...c, _targetDateTime: base };
      })
      .filter(Boolean);

    if (withDates.length === 0) return null;

    withDates.sort((a, b) => a._targetDateTime - b._targetDateTime);
    return withDates[0];
  }, [acceptedConsultationsRaw]);

  // Event reminder: fetch bookings from today through 5 days ahead (5th, 4th, 3rd, 2nd, 1st/last day)
  const getDateRange = () => {
    const today = new Date();
    const start = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const end = new Date(today);
    end.setDate(end.getDate() + 5);
    const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
    return { start, end: endStr };
  };
  const { start: reminderStartDate, end: reminderEndDate } = getDateRange();
  const { data: eventReminderData } = useQuery({
    queryKey: ['vendorBookings', 'eventReminder', reminderStartDate, reminderEndDate],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings', {
        params: { start_date: reminderStartDate, end_date: reminderEndDate, per_page: 50 }
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const eventReminderBookingsRaw = (eventReminderData?.data?.data || eventReminderData?.data || []).filter(
    (b) => b.status !== 'cancelled'
  );
  // Group by days remaining: 5, 4, 3, 2, 1, 0 (today)
  const getDaysRemaining = (eventDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const evt = new Date(eventDate);
    evt.setHours(0, 0, 0, 0);
    return Math.round((evt - today) / (24 * 60 * 60 * 1000));
  };
  const eventReminderByDays = eventReminderBookingsRaw.reduce((acc, b) => {
    const days = getDaysRemaining(b.event_date);
    if (days >= 0 && days <= 5) {
      if (!acc[days]) acc[days] = [];
      acc[days].push(b);
    }
    return acc;
  }, {});
  const fiveDayReminderBookings = eventReminderBookingsRaw; // flat list for modal

  // Fetch subscription details when component mounts or subscription status changes
  useEffect(() => {
    if (hasActiveSubscription === true) {
      refetchSubscription();
    }
  }, [hasActiveSubscription, user?.id]);

  // Auto-open 5-day event reminder modal once per day
  useEffect(() => {
    if (fiveDayReminderBookings.length === 0) return;
    const today = new Date().toDateString();
    const storageKey = `fiveDayReminderModal_${today}`;
    const alreadyShown = localStorage.getItem(storageKey);
    if (!alreadyShown) {
      setShowEventReminderModal(true);
    }
  }, [fiveDayReminderBookings.length]);

  const handleCloseEventReminderModal = () => {
    const today = new Date().toDateString();
    localStorage.setItem(`fiveDayReminderModal_${today}`, '1');
    setShowEventReminderModal(false);
  };

  // Helper functions
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
    return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300';
      case 'confirmed':
        return 'bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300';
      case 'pending':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
      case 'in_progress':
        return 'bg-primary-100 text-primary-800 dark:bg-primary-950/40 dark:text-primary-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  // Check if subscription is expiring soon
  const isExpiringSoon = () => {
    if (!subscriptionDetails?.days_remaining) return false;
    return subscriptionDetails.days_remaining <= 5 && subscriptionDetails.days_remaining > 0;
  };

  const getUrgencyLevel = () => {
    if (!subscriptionDetails?.days_remaining) return null;
    if (subscriptionDetails.days_remaining <= 1) return 'critical';
    if (subscriptionDetails.days_remaining <= 3) return 'high';
    if (subscriptionDetails.days_remaining <= 5) return 'medium';
    return null;
  };

  const handleRenewalSuccess = async () => {
    await refetchSubscription();
    checkSubscriptionStatus();
  };

  if (isChecking || loading || verificationLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  const periodTabs = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'quarter', label: 'Quarter' },
    { value: 'year', label: 'Year' },
  ];

  const primaryMetrics = [
    {
      label: 'Total revenue',
      value: formatCurrency(analytics.total_revenue || 0),
      hint: 'Completed bookings',
      icon: DollarSign,
      accent: 'text-emerald-600 dark:text-emerald-400',
      wrap: 'bg-emerald-50 dark:bg-emerald-950/30',
      isAmount: true,
    },
    {
      label: 'Total bookings',
      value: analytics.total_bookings ?? stats.total_bookings ?? 0,
      hint: 'All statuses',
      icon: Calendar,
      accent: 'text-sky-600 dark:text-sky-400',
      wrap: 'bg-sky-50 dark:bg-sky-950/30',
      isAmount: false,
    },
    {
      label: 'Avg. booking value',
      value: formatCurrency(analytics.average_booking_value || 0),
      hint: 'Per completed booking',
      icon: Star,
      accent: 'text-amber-600 dark:text-amber-400',
      wrap: 'bg-amber-50 dark:bg-amber-950/30',
      isAmount: true,
    },
    {
      label: 'Pending requests',
      value: stats.pending_bookings || 0,
      hint: 'Awaiting your action',
      icon: Clock,
      accent: 'text-rose-600 dark:text-rose-400',
      wrap: 'bg-rose-50 dark:bg-rose-950/30',
      isAmount: false,
    },
  ].filter((m) => amountsVisible || !m.isAmount);

  const statusMetrics = [
    { label: 'Confirmed', value: stats.confirmed_bookings || 0 },
    { label: 'In progress', value: stats.in_progress_bookings || 0 },
    { label: 'Completed', value: stats.completed_bookings || 0 },
    { label: 'Cancelled', value: stats.cancelled_bookings || 0 },
  ];

  return (
    <div className="space-y-8 pb-10">
      <VendorPageHeader
        className="mb-0"
        title="Dashboard"
        subtitle={`Welcome back, ${user?.name || 'Vendor'}. Here’s what’s happening with your business.`}
        actions={
          <>
            <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1 shadow-sm">
              {periodTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setPeriod(tab.value)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                    period === tab.value
                      ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <DashboardAmountVisibilityToggle
              amountsVisible={amountsVisible}
              onToggle={toggleAmountsVisible}
            />
            <button
              type="button"
              onClick={() => refetchAnalytics()}
              disabled={analyticsFetching}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${analyticsFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {hasActiveSubscription && subscriptionDetails ? (
              <Link
                to="/vendor/my-subscription"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 !rounded-xl hover:bg-black dark:hover:bg-white transition-colors text-sm font-medium shadow-sm"
              >
                <Crown className="w-4 h-4" />
                <span className="truncate max-w-[10rem]">{subscriptionDetails.plan_name || 'Subscription'}</span>
                {subscriptionDetails.days_remaining != null ? (
                  <span className="hidden sm:inline-flex px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-white/15 dark:bg-gray-900/10">
                    {subscriptionDetails.days_remaining}d left
                  </span>
                ) : null}
              </Link>
            ) : null}
          </>
        }
      />

      <div className="flex flex-wrap gap-2">
        {[
          { to: '/vendor/bookings', label: 'Bookings', Icon: Calendar },
          { to: '/vendor/consultations', label: 'Consultations', Icon: Video },
          { to: '/vendor/payout-disbursements', label: 'Payouts', Icon: Banknote },
          { to: '/vendor/analytics', label: 'Analytics', Icon: BarChart3 },
        ].map(({ to, label, Icon }) => (
          <Link
            key={to}
            to={to}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <Icon className="w-3.5 h-3.5 text-gray-500" />
            {label}
          </Link>
        ))}
      </div>

      {showGettingStarted && (
        <VendorGettingStarted
          steps={gettingStartedSteps}
          onDismiss={dismissGettingStarted}
        />
      )}

      {upcomingConsultation && (
        <ConsultationCountdownBanner consultation={upcomingConsultation} />
      )}

      {!verificationLoading && (
        <VendorAttentionStack
          isVendorVerified={isVendorVerified}
          isVerificationPending={isVerificationPending}
          onStartVerification={() => navigate('/vendor/verification')}
          hasActiveSubscription={hasActiveSubscription}
          subscriptionDetails={subscriptionDetails}
          isExpiringSoon={isExpiringSoon()}
          urgencyLevel={getUrgencyLevel()}
          onRenew={() => setShowRenewalModal(true)}
          onComparePlans={() => navigate('/vendor/subscription')}
          pendingConsultationCount={pendingConsultationCount}
          onViewConsultations={() => navigate('/vendor/consultations?status=pending')}
          fiveDayReminderBookings={fiveDayReminderBookings}
          eventReminderByDays={eventReminderByDays}
          onOpenReminder={() => setShowEventReminderModal(true)}
          onViewBookings={() => navigate('/vendor/bookings')}
        />
      )}

      {announcements.length > 0 && (
        <section className="space-y-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
              From the platform
            </p>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 tracking-tight">
              Announcements
            </h2>
          </div>
          <div className="space-y-3">
            {announcements.slice(0, 3).map((announcement) => (
              <AnnouncementCard key={announcement.id} announcement={announcement} />
            ))}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
              Performance
            </p>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 tracking-tight">
              Key metrics
            </h2>
          </div>
          {analyticsFetching ? (
            <span className="text-xs text-gray-400 inline-flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Updating…
            </span>
          ) : !amountsVisible ? (
            <span className="text-xs text-gray-400">Amount cards hidden</span>
          ) : null}
        </div>

        {!amountsVisible ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Revenue cards are hidden. Use the eye control in the header to show them again.
          </p>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {primaryMetrics.map((m) => {
            const Icon = m.icon;
            return (
              <div
                key={m.label}
                className="rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900 p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{m.label}</p>
                    <p className="mt-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50 tabular-nums">
                      {m.value}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{m.hint}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${m.wrap}`}>
                    <Icon className={`w-5 h-5 ${m.accent}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {statusMetrics.map((m) => (
              <div key={m.label} className="rounded-xl bg-gray-50 dark:bg-gray-800/60 px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {m.label}
                </p>
                <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-50 tabular-nums">
                  {m.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <section className="xl:col-span-2 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                Activity
              </p>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 tracking-tight">
                Recent bookings
              </h2>
            </div>
            <Link
              to="/vendor/bookings"
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 inline-flex items-center gap-1"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
            {bookingsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : recentBookings.length === 0 ? (
              <div className="text-center py-16 px-6">
                <Calendar className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No bookings yet</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                  When clients book your packages, they’ll show up here.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {recentBookings.slice(0, 5).map((booking) => (
                  <div
                    key={booking.id}
                    className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">
                          {booking.client?.name || booking.client_name || 'Client'}
                        </p>
                        {isPrivateBooking(booking) && <PrivateBookingBadge />}
                        <span
                          className={`inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full ${getStatusColor(booking.status)}`}
                        >
                          {booking.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                        {booking.package?.name || 'Package'} · {formatDate(booking.event_date)}
                        {booking.event_time ? ` · ${booking.event_time}` : ''}
                      </p>
                      {isPrivateBooking(booking) && (
                        <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">
                          {PRIVATE_PAYOUT_EXCLUDED_NOTE}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-50 tabular-nums">
                        {amountsVisible
                          ? formatCurrency(
                              booking.vendor_payout_amount ??
                                booking.subtotal_amount ??
                                booking.total_amount ??
                                0
                            )
                          : '••••'}
                      </p>
                      <Link
                        to={`/vendor/bookings/${booking.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Open
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
              Insights
            </p>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 tracking-tight">
              Platform performance
            </h2>
          </div>
          <PlatformPerformanceCard
            performance={analytics.platform_performance}
          />
        </section>
      </div>

      <EventReminderModal
        isOpen={showEventReminderModal}
        onClose={handleCloseEventReminderModal}
        bookingsByDays={eventReminderByDays}
        bookings={fiveDayReminderBookings}
      />

      <RenewalModal
        isOpen={showRenewalModal}
        onClose={() => setShowRenewalModal(false)}
        subscriptionDetails={subscriptionDetails}
        onSuccess={handleRenewalSuccess}
      />
    </div>
  );
};

export default VendorDashboard;
